import { colors } from "@ardoq/design-tokens";
import { range, isNumber } from "lodash";
import { tint, shade, darken as darken$1, hsl, lighten, parseToRgb, rgbToColorString, parseToHsl, getContrast, getLuminance, rgba } from "polished";
import { createFifoCache } from "@ardoq/common-helpers";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { logWarn, logError } from "@ardoq/logging";
import styled from "styled-components";
const RGBA_OBJ_WHITE = {
  get red() {
    return 255;
  },
  get green() {
    return 255;
  },
  get blue() {
    return 255;
  },
  get alpha() {
    return 1;
  }
};
const COLOR_NAME_TO_RGBA_MAP = /* @__PURE__ */ new Map([
  ["black", { red: 0, green: 0, blue: 0, alpha: 1 }],
  ["blue", { red: 0, green: 0, blue: 255, alpha: 1 }],
  ["darkblue", { red: 0, green: 0, blue: 139, alpha: 1 }],
  ["gray", { red: 128, green: 128, blue: 128, alpha: 1 }],
  ["green", { red: 0, green: 128, blue: 0, alpha: 1 }],
  ["magenta", { red: 255, green: 0, blue: 255, alpha: 1 }],
  ["orange", { red: 255, green: 165, blue: 0, alpha: 1 }],
  ["red", { red: 255, green: 0, blue: 0, alpha: 1 }],
  ["yellow", { red: 255, green: 255, blue: 0, alpha: 1 }]
]);
const chartPalette = [
  colors.blue50,
  colors.yellow60,
  colors.purple25,
  colors.green50
];
const tintValue = (value) => 0.75 - 1 / (value + 1);
const getRangeColor = (index) => index < chartPalette.length ? chartPalette[index] : tint(
  tintValue(Math.floor(index / chartPalette.length)),
  chartPalette[index % chartPalette.length]
);
const getColorRange = (howMany = 1) => range(0, howMany).map(getRangeColor);
const colorRange = getColorRange(5);
const getDefaultLevelColors = () => ({
  level1: colorRange[0],
  level2: colorRange[1],
  level3: colorRange[2],
  level4: colorRange[3],
  level5: colorRange[4]
});
const isValidColorHex = (hex = "") => {
  return /^#?([a-fA-F]|\d){3,8}$/.test(hex);
};
const DEFAULT_COLORS = getDefaultLevelColors();
const rgbRegex = /^ *rgb\( *([\d., ]*) *\) *$/i;
const rgbaRegex = /^ *rgba\( *([\d., ]*) *\) *$/i;
const darken = darken$1;
const getDefaultCSSColor = (level) => {
  const cssColorsAsRecord = DEFAULT_COLORS;
  return cssColorsAsRecord[`level${level}`] || getRangeColor(level);
};
const isNumberInRange = (maybeNumber, min, max) => isNumber(maybeNumber) && maybeNumber >= min && maybeNumber <= max;
const isValidHexColor = (hexColor) => {
  if (!hexColor.startsWith("#")) return false;
  try {
    const { red, green, blue } = parseToRgb(hexColor);
    return isNumberInRange(red, 0, 255) && isNumberInRange(green, 0, 255) && isNumberInRange(blue, 0, 255);
  } catch {
    return false;
  }
};
const isValidRgbString = (rgbString) => {
  const regexResults = rgbRegex.exec(rgbString);
  if (!regexResults) return false;
  const [red, green, blue] = regexResults[1].split(",").map((str) => str.trim()).map(parseFloat);
  return isNumberInRange(red, 0, 255) && isNumberInRange(green, 0, 255) && isNumberInRange(blue, 0, 255);
};
const isValidRgbaString = (rgbaString) => {
  const regexResults = rgbaRegex.exec(rgbaString);
  if (!regexResults) return false;
  const [red, green, blue, alpha] = regexResults[1].split(",").map((str) => str.trim()).map(parseFloat);
  return isNumberInRange(red, 0, 255) && isNumberInRange(green, 0, 255) && isNumberInRange(blue, 0, 255) && isNumberInRange(alpha, 0, 1);
};
const hslToHsv = ({
  hue,
  hslSaturation,
  lightness
}) => {
  const value = hslSaturation * Math.min(lightness, 1 - lightness) + lightness;
  const hsvSaturation = value ? 2 - 2 * lightness / value : 0;
  return { hue, hsvSaturation, value };
};
const hsvToHsl = ({
  hue,
  hsvSaturation,
  value
}) => {
  const lightness = value - value * hsvSaturation / 2;
  const m = Math.min(lightness, 1 - lightness);
  const hslSaturation = m ? (value - lightness) / m : 0;
  return { hue, hslSaturation, lightness };
};
const getFullHexFrom3Digit = (hexColor) => [
  "#",
  ...hexColor.slice(1).split("").map((hexChar) => hexChar.repeat(2))
].join("");
const transparencyToHexValue = (alpha) => {
  const hexValue = Math.round(alpha * 255).toString(16).toUpperCase();
  return hexValue.length < 2 ? `0${hexValue}` : hexValue;
};
const getHexWithTransparency = ({
  red,
  green,
  blue,
  alpha
}) => {
  const hexColorNoTransparency = rgbToColorString({ red, green, blue });
  const hexColorNoTransparencyExpanded = hexColorNoTransparency.length === 4 ? getFullHexFrom3Digit(hexColorNoTransparency) : hexColorNoTransparency;
  const hexTransparency = alpha < 1 ? transparencyToHexValue(alpha) : "";
  return `${hexColorNoTransparencyExpanded}${hexTransparency}`;
};
const rgbaStringToObj = (rgbaStr) => {
  const regexResults = rgbaRegex.exec(rgbaStr);
  if (!(regexResults && isValidRgbaString(rgbaStr))) return RGBA_OBJ_WHITE;
  const [red, green, blue, alpha] = regexResults[1].split(",").map((str) => str.trim()).map(parseFloat);
  return { red, green, blue, alpha };
};
const rgbStringToRgbaObj = (rgbStr) => {
  const regexResults = rgbRegex.exec(rgbStr);
  if (!(regexResults && isValidRgbString(rgbStr))) return RGBA_OBJ_WHITE;
  const [red, green, blue] = regexResults[1].split(",").map((str) => str.trim()).map(parseFloat);
  return { red, green, blue, alpha: 1 };
};
const hexColorToRgbaObj = (hexColor) => {
  const { red, green, blue, alpha } = { alpha: 1, ...parseToRgb(hexColor) };
  return { red, green, blue, alpha };
};
const rgbaObjToStr = ({ red, green, blue, alpha }) => `rgba(${red},${green},${blue},${alpha})`;
const unknownColorTypeStrToRgbaObj = (colorString) => {
  if (isValidRgbaString(colorString)) return rgbaStringToObj(colorString);
  if (isValidRgbString(colorString)) return rgbStringToRgbaObj(colorString);
  if (isValidHexColor(colorString)) return hexColorToRgbaObj(colorString);
  if (COLOR_NAME_TO_RGBA_MAP.has(colorString))
    return COLOR_NAME_TO_RGBA_MAP.get(colorString);
  return RGBA_OBJ_WHITE;
};
const getValidHexOrRgbaColor = (colorString) => {
  const colorIsHex = isValidHexColor(colorString);
  const colorIsRgba = isValidRgbaString(colorString);
  if (colorIsHex || colorIsRgba) return colorString;
  return void 0;
};
const getSanitizedRgbaClassName = (rgbaString) => rgbaString.toLowerCase().replace("rgba(", "filter").replace(/\./g, "-").replace(/,/g, "_").replace(")", "");
const COMPONENT_BACKGROUND_LIGHT = 0.8;
const shadedColor = shade(0.25);
const getShadedColor = (color) => {
  try {
    return shadedColor(color);
  } catch {
    return "#ffffff";
  }
};
const getLightTintedColor = tint(COMPONENT_BACKGROUND_LIGHT);
const getLightenedColor = createFifoCache(500, (color) => {
  const rgba2 = rgbaStringToObj(color);
  if (rgba2 !== RGBA_OBJ_WHITE && 0 < rgba2.alpha && rgba2.alpha < 1) {
    return `rgba(${rgba2.red}, ${rgba2.green}, ${rgba2.blue}, ${((1 - COMPONENT_BACKGROUND_LIGHT) * rgba2.alpha).toFixed(3)})`;
  }
  try {
    return getLightTintedColor(color);
  } catch {
    return "#ffffff";
  }
});
const normalizeColorToHex = (unknownColorValue) => getHexWithTransparency(unknownColorTypeStrToRgbaObj(unknownColorValue));
const tryParseColorStringToHex = (unknownColorValue) => {
  try {
    return unknownColorValue && parseToRgb(unknownColorValue) ? normalizeColorToHex(unknownColorValue) : null;
  } catch (error) {
    return null;
  }
};
const getIsCssColorValue = /* @__PURE__ */ (() => {
  let testSpan = null;
  let testContainer = null;
  const cache = /* @__PURE__ */ new Map();
  const TEST_CONTAINER_ID = "assets-and-test-container";
  const colorPrefixes = ["#", "rgb", "hsl"];
  return (str, { ignoreTransparencies } = {}) => {
    if (ignoreTransparencies && str.startsWith("#") && // #cccc - color with transparency
    // #cccccccc - color with transparency
    [5, 9].includes(str.length)) {
      return false;
    }
    if (!colorPrefixes.some((prefix) => str.startsWith(prefix))) {
      return false;
    }
    if (cache.has(str)) {
      return cache.get(str);
    }
    if (document && !testContainer) {
      testContainer = document.querySelector(`#${TEST_CONTAINER_ID}`);
      if (!testContainer) {
        testContainer = document.createElement("div");
        document.body.append(testContainer);
        testContainer.id = TEST_CONTAINER_ID;
        testContainer.style.cssText = `
          height: 0;
          left: -1000px;
          pointer-events: none;
          position: absolute;
          top: 0;
          width: 0;
        `;
      }
    }
    if (testContainer && !testSpan) {
      testSpan = document.createElement("span");
      testContainer.appendChild(testSpan);
    }
    if (testSpan) {
      testSpan.style.removeProperty("background-color");
      const backgroundColor = window.getComputedStyle(testSpan).getPropertyValue("background-color");
      testSpan.style.backgroundColor = str;
      const isColorValue = window.getComputedStyle(testSpan).getPropertyValue("background-color") !== backgroundColor;
      cache.set(str, isColorValue);
      return isColorValue;
    }
    return false;
  };
})();
const hexToHsl = (hex) => {
  const { hue, saturation, lightness } = parseToHsl(hex);
  return { hue, hslSaturation: saturation * 100, lightness: lightness * 100 };
};
const hslToHex = ({
  hue,
  hslSaturation,
  lightness
}) => hsl(hue, hslSaturation / 100, lightness / 100);
const rgbToHSL = ({ red, green, blue }) => {
  const hsl2 = parseToHsl(`rgb(${red}, ${green}, ${blue})`);
  return {
    hue: hsl2.hue,
    hslSaturation: hsl2.saturation * 100,
    lightness: hsl2.lightness * 100
  };
};
const isValidRgbObject = (rgbObj) => Boolean(
  rgbObj && rgbObj.red !== void 0 && rgbObj.green !== void 0 && rgbObj.blue !== void 0
);
const getFeColorMatrixValuesForColor = (color) => {
  const rgbObj = parseToRgb(color);
  if (isValidRgbObject(rgbObj)) {
    const { red, green, blue } = rgbObj;
    return getImageColorMatrixValuesForRgb(`${red}~~${green}~~${blue}`);
  }
};
const convertAlphaChannelValueToColor = (channelValue) => channelValue ** 2 * 0.86;
const MINIMAL_GRAY_SCALE = [0.1, 0.1, 0.1];
const getImageColorMatrixValuesForRgb = createFifoCache(
  500,
  (rgbColorStringFifoCacheArgs) => {
    const rgb = rgbColorStringFifoCacheArgs.split("~~").map((v) => Number(v) / 255);
    const [red, green, blue] = rgb;
    const feColorMatrix = [
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(red), 0],
      // r channel
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(green), 0],
      // g channel
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(blue), 0],
      // b channel
      [0, 0, 0, 1, 0]
      // alpha
    ];
    return feColorMatrix.map((channel) => channel.join(",")).join(",");
  }
);
const calcLuminance = ({ red, green, blue }) => {
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
};
const isLightColor = (color) => {
  const rgb = parseToRgb(color);
  const luminance = calcLuminance(rgb);
  return luminance > 0.5;
};
const adaptiveColorAdjustment = (color, percentage) => {
  return isLightColor(color) ? darken(percentage, color) : lighten(percentage, color);
};
const readableText = (color) => {
  return isLightColor(color) ? "#000000" : "#ffffff";
};
const IMAGE_COLOR_FILTERS_CONTAINER_ID = "image-color-filters-container";
const ImageColorFilterWithMatrix = ({
  color
}) => /* @__PURE__ */ jsx("filter", { id: getImageColorFilterId(color), children: /* @__PURE__ */ jsx(
  "feColorMatrix",
  {
    in: "SourceGraphic",
    type: "matrix",
    result: "first",
    values: getFeColorMatrixValuesForColor(color)
  }
) });
const getImageColorFilterId = (color) => `image-color-filter-${normalizeColorToHex(color)}`;
const ColorFilters = ({ colors: colors2 }) => /* @__PURE__ */ jsx(
  "svg",
  {
    viewBox: "0 0 0 0",
    style: { width: 0, height: 0, position: "absolute" },
    id: IMAGE_COLOR_FILTERS_CONTAINER_ID,
    children: colors2.map((color) => /* @__PURE__ */ jsx(
      ImageColorFilterWithMatrix,
      {
        color
      },
      getImageColorFilterId(color)
    ))
  }
);
const TARGET_CONTRAST = 3;
const PRECISION = 1 / 255 ** 3;
const darkenOrLighten = (fixedColor, targetColor, darken2) => {
  let shadeAmount = 1;
  let goodShadeAmount = 2;
  let goodContrast = NaN;
  let currentColor = targetColor;
  while (Math.abs(goodShadeAmount - shadeAmount) > PRECISION) {
    const color = darken2 ? shade(shadeAmount, targetColor) : tint(shadeAmount, targetColor);
    const contrast = getContrast(fixedColor, color);
    if (contrast < TARGET_CONTRAST) {
      if (darken2 && color === "#000") {
        return darkenOrLighten(fixedColor, targetColor, false);
      }
      if (!darken2 && color === "#fff") {
        logError(Error("unable to find contrast color."), null, {
          fixedColor,
          targetColor
        });
        return getLuminance(fixedColor) < 0.5 ? "white" : "black";
      }
      shadeAmount = shadeAmount + (goodShadeAmount - shadeAmount) / 2;
      continue;
    }
    if (contrast === TARGET_CONTRAST || goodContrast === contrast) {
      return color;
    }
    goodContrast = contrast;
    currentColor = color;
    const nextShadeAmount = shadeAmount - (goodShadeAmount - shadeAmount) / 2;
    goodShadeAmount = shadeAmount;
    shadeAmount = nextShadeAmount;
  }
  return currentColor;
};
const ensureContrast = (fixedColor, targetColor) => {
  try {
    [fixedColor, targetColor].forEach(parseToRgb);
  } catch {
    logWarn(Error("Unrecognized color strings."), null, {
      fixedColor,
      targetColor
    });
    return targetColor;
  }
  if (getContrast(fixedColor, targetColor) >= TARGET_CONTRAST) {
    return targetColor;
  }
  return darkenOrLighten(fixedColor, targetColor, true);
};
const cachingEnsureContrast = createFifoCache(
  500,
  // createFifoCache only takes a single argument, so we have to concatenate the colors here.
  (colors2) => {
    const [fixedColor, targetColor] = colors2.split("\\");
    return ensureContrast(fixedColor, targetColor);
  }
);
const ensureContrast$1 = (fixedColor, targetColor) => cachingEnsureContrast(`${fixedColor}\\${targetColor}`);
const getFillAndStroke = (color, useAsBackgroundStyle) => {
  if (!useAsBackgroundStyle) {
    return { fill: color, stroke: color };
  }
  const lightenedColor = getLightenedColor(color);
  return {
    fill: lightenedColor,
    stroke: ensureContrast$1(lightenedColor, color)
  };
};
const OffScreenSvg = styled.svg`
  position: absolute;
  left: -1000px;
  top: -1000px;
  width: 0;
  height: 0;
`;
const GraphVisualizationFilters = () => /* @__PURE__ */ jsx(OffScreenSvg, { children: /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(GraphVisualizationFilterElements, {}) }) });
const clampByte = (byte) => Math.min(255, Math.max(0, Math.round(byte)));
const applyMatrix = (color, matrix) => {
  const { red, green, blue, alpha } = { alpha: 1, ...parseToRgb(color) };
  const [
    [r1, r2, r3, r4, r5],
    [g1, g2, g3, g4, g5],
    [b1, b2, b3, b4, b5],
    [a1, a2, a3, a4, a5]
  ] = matrix;
  const alphaAsByte = alpha * 255;
  return rgba(
    clampByte(r1 * red + r2 * green + r3 * blue + r4 * alphaAsByte + r5),
    clampByte(g1 * red + g2 * green + g3 * blue + g4 * alphaAsByte + g5),
    clampByte(b1 * red + b2 * green + b3 * blue + b4 * alphaAsByte + b5),
    Math.min(
      1,
      Math.max(
        0,
        Math.round(a1 * red + a2 * green + a3 * blue + a4 * alpha + a5)
      )
    )
  );
};
const VISUAL_DIFF_FOREGROUND = "#404040";
const VISUAL_DIFF_BACKGROUND = "#e6e6e6";
const UNCHANGED_MATRIX = [
  [0.299, 0.587, 0.114, 0.467, 0],
  [0.299, 0.587, 0.114, 0.525, 0],
  [0.299, 0.587, 0.114, 0.612, 0],
  [0, 0, 0, 1, 0]
];
const visualDiffFilterMatrices = {
  "visual-diff-added": [
    [0.206, 0.405, 0.079, 0.298, 0],
    [0.206, 0.405, 0.079, 0.714, 0],
    [0.206, 0.405, 0.079, 0.314, 0],
    [0, 0, 0, 1, 0]
  ],
  "visual-diff-changed": [
    [0.182, 0.358, 0.07, 0.082, 0],
    [0.182, 0.358, 0.07, 0.455, 0],
    [0.182, 0.358, 0.07, 1, 0],
    [0, 0, 0, 1, 0]
  ],
  "visual-diff-removed": [
    [0.23, 0.452, 0.088, 1, 0],
    [0.23, 0.452, 0.088, 0.082, 0],
    [0.23, 0.452, 0.088, 0.082, 0],
    [0, 0, 0, 1, 0]
  ],
  "visual-diff-unchanged": UNCHANGED_MATRIX,
  "visual-diff-placeholder": UNCHANGED_MATRIX
};
const visualDiffColors = Object.fromEntries(
  Object.entries(visualDiffFilterMatrices).map(
    ([visualDiffClassName, matrix]) => [
      visualDiffClassName,
      applyMatrix(VISUAL_DIFF_FOREGROUND, matrix)
    ]
  )
);
const visualDiffBackgroundColors = Object.fromEntries(
  Object.entries(visualDiffColors).map(([visualDiffClassName, foreground]) => [
    visualDiffClassName,
    getLightenedColor(foreground)
  ])
);
const GraphVisualizationFilterElements = () => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx("filter", { id: "visual-diff-filter-added", children: /* @__PURE__ */ jsx(
    "feColorMatrix",
    {
      type: "matrix",
      values: `${visualDiffFilterMatrices["visual-diff-added"]}`
    }
  ) }),
  /* @__PURE__ */ jsx("filter", { id: "visual-diff-filter-changed", children: /* @__PURE__ */ jsx(
    "feColorMatrix",
    {
      type: "matrix",
      values: `${visualDiffFilterMatrices["visual-diff-changed"]}`
    }
  ) }),
  /* @__PURE__ */ jsx("filter", { id: "visual-diff-filter-removed", children: /* @__PURE__ */ jsx(
    "feColorMatrix",
    {
      type: "matrix",
      values: `${visualDiffFilterMatrices["visual-diff-removed"]}`
    }
  ) }),
  /* @__PURE__ */ jsx("filter", { id: "visual-diff-filter-unchanged", children: /* @__PURE__ */ jsx(
    "feColorMatrix",
    {
      type: "matrix",
      values: `${visualDiffFilterMatrices["visual-diff-unchanged"]}`
    }
  ) })
] });
const referenceModelClassName = (modelId) => `m${modelId}`;
const referenceTypeClassName = (referenceTypeId) => `nr${referenceTypeId}`;
const filterClass = (filterColor) => filterColor.startsWith("#") ? filterColor.replace("#", "filter") : filterColor.toLowerCase().startsWith("rgba") ? getSanitizedRgbaClassName(filterColor) : void 0;
export {
  COLOR_NAME_TO_RGBA_MAP,
  ColorFilters,
  DEFAULT_COLORS,
  GraphVisualizationFilterElements,
  GraphVisualizationFilters,
  IMAGE_COLOR_FILTERS_CONTAINER_ID,
  OffScreenSvg,
  RGBA_OBJ_WHITE,
  VISUAL_DIFF_BACKGROUND,
  VISUAL_DIFF_FOREGROUND,
  adaptiveColorAdjustment,
  darken,
  ensureContrast$1 as ensureContrast,
  filterClass,
  getColorRange,
  getDefaultCSSColor,
  getFeColorMatrixValuesForColor,
  getFillAndStroke,
  getImageColorFilterId,
  getIsCssColorValue,
  getLightenedColor,
  getRangeColor,
  getSanitizedRgbaClassName,
  getShadedColor,
  getValidHexOrRgbaColor,
  hexColorToRgbaObj,
  hexToHsl,
  hslToHex,
  hslToHsv,
  hsvToHsl,
  isValidColorHex,
  isValidHexColor,
  normalizeColorToHex,
  readableText,
  referenceModelClassName,
  referenceTypeClassName,
  rgbToHSL,
  rgbaObjToStr,
  rgbaStringToObj,
  tryParseColorStringToHex,
  unknownColorTypeStrToRgbaObj,
  visualDiffBackgroundColors,
  visualDiffColors
};
//# sourceMappingURL=index.js.map
